import React, { useState, useEffect } from 'react'
import { Card, Table, Pagination, Button, Space, DatePicker, Typography, Row, Col, Divider, Tag, Select, Avatar } from 'antd'
import { DownloadOutlined, FilterOutlined, ReloadOutlined, DollarOutlined, UserOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import useLedgersReport from '../Hooks/Reports/useLedgersReport'
import { LedgerSortEnum } from 'Common/Constants/Enums/Individual/Reports/LedgerSortByEnum'
import { PaymentSourceTypeEnum, PaymentSourceTypeLabels, PaymentSourceTypeColors } from 'Common/Constants/Enums/PaymentSourceTypeEnum'
import { PaymentStatusEnum, PaymentStatusLabels, PaymentStatusColors } from 'Common/Constants/Enums/PaymentStatusEnum'

const { RangePicker } = DatePicker
const { Title, Text } = Typography
const { Option } = Select

export default function LedgersReport() {
  const {
    data, loading, isDownloading, pageNumber, pageSize, totalCount,
    filters, setFilters, setPageNumber, setPageSize, fetchReport, downloadReport,
  } = useLedgersReport()

  const [localFilters, setLocalFilters] = useState({ ...filters })

  // The ONLY effect that fetches data
  useEffect(() => {
    const params = {
      PageNumber: pageNumber,
      PageSize: pageSize,
      SortBy: filters.SortBy,
    }

    if (filters.FromDate) params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD')
    if (filters.ToDate) params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD')
    if (filters.SourceType !== null) params.SourceType = filters.SourceType
    if (filters.Status !== null) params.Status = filters.Status

    fetchReport(params)
  }, [pageNumber, pageSize, filters]) 

  const handleApply = () => {
    setPageNumber(1)
    setFilters({ ...localFilters })
  }

  const handleReset = () => {
    const defaults = {
      SortBy: LedgerSortEnum.Latest,
      FromDate: null,
      ToDate: null,
      SourceType: null,
      Status: null,
    }
    setLocalFilters(defaults)
    setPageNumber(1)
    setFilters(defaults)
  }

  const handleDownload = () => {
    const params = { SortBy: filters.SortBy }
    if (filters.FromDate) params.FromDate = dayjs(filters.FromDate).format('YYYY-MM-DD')
    if (filters.ToDate) params.ToDate = dayjs(filters.ToDate).endOf('day').format('YYYY-MM-DD')
    if (filters.SourceType !== null) params.SourceType = filters.SourceType
    if (filters.Status !== null) params.Status = filters.Status
    downloadReport(params)
  }

  const columns = [
    {
      title: 'Learner',
      key: 'learner',
      render: (_, record) => (
        <Space>
          <Avatar src={record.learner?.profileImage} icon={<UserOutlined />} />
          <div>
            <div>{record.learner?.firstName} {record.learner?.lastName}</div>
            <Text type="secondary">{record.learner?.learnerCode}</Text>
          </div>
        </Space>
      ),
    },
    { title: 'Email', dataIndex: ['learner', 'email'], key: 'email' },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount, record) => (
        <Tag color="green" icon={<DollarOutlined />}>
          {amount} {record.currency}
        </Tag>
      ),
    },
    {
      title: 'Source Type',
      dataIndex: 'sourceType',
      key: 'sourceType',
      render: (type) => (
        <Tag color={PaymentSourceTypeColors[type]}>{PaymentSourceTypeLabels[type]}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={PaymentStatusColors[status]}>{PaymentStatusLabels[status]}</Tag>
      ),
    },
    { title: 'Payment Provider', dataIndex: 'paymentProvider', key: 'paymentProvider' },
    {
      title: 'Paid At',
      dataIndex: 'paidAt',
      key: 'paidAt',
      render: (date) => dayjs(date).format('DD MMM YYYY, HH:mm'),
    },
  ]

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <Title level={2}>Financial Report</Title>
          <Text type="secondary">Payment transactions and financial records</Text>
        </div>
        <Space>
          <Button onClick={() => window.history.back()}>Back</Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload} loading={isDownloading}>
            Download
          </Button>
        </Space>
      </div>

      <Card title={<Space><FilterOutlined />Filters</Space>}>
        <Row gutter={16}>
          <Col md={8}>
            <Text strong>Date Range</Text>
            <RangePicker
              style={{ width: '100%', marginTop: 6 }}
              value={localFilters.FromDate ? [dayjs(localFilters.FromDate), dayjs(localFilters.ToDate)] : null}
              onChange={(d) => setLocalFilters({ ...localFilters, FromDate: d?.[0]?.toDate() || null, ToDate: d?.[1]?.toDate() || null })}
            />
          </Col>
          <Col md={5}>
            <Text strong>Source Type</Text>
            <Select 
              style={{ width: '100%', marginTop: 6 }} 
              value={localFilters.SourceType} 
              onChange={(v) => setLocalFilters({ ...localFilters, SourceType: v })}
              allowClear placeholder="All Sources"
            >
              {Object.keys(PaymentSourceTypeEnum).map(key => (
                <Option key={key} value={PaymentSourceTypeEnum[key]}>{PaymentSourceTypeLabels[PaymentSourceTypeEnum[key]]}</Option>
              ))}
            </Select>
          </Col>
          <Col md={5}>
            <Text strong>Status</Text>
            <Select 
              style={{ width: '100%', marginTop: 6 }} 
              value={localFilters.Status} 
              onChange={(v) => setLocalFilters({ ...localFilters, Status: v })}
              allowClear placeholder="All Statuses"
            >
              {Object.keys(PaymentStatusEnum).map(key => (
                <Option key={key} value={PaymentStatusEnum[key]}>{PaymentStatusLabels[PaymentStatusEnum[key]]}</Option>
              ))}
            </Select>
          </Col>
          <Col md={6}>
            <Text strong>Sort By</Text>
            <Select style={{ width: '100%', marginTop: 6 }} value={localFilters.SortBy} onChange={(v) => setLocalFilters({ ...localFilters, SortBy: v })}>
              <Option value={LedgerSortEnum.Latest}>Latest</Option>
              <Option value={LedgerSortEnum.Amount}>Amount</Option>
            </Select>
          </Col>
        </Row>
        <Divider />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>Reset</Button>
          <Button type="primary" onClick={handleApply}>Apply</Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }}>
        <Table rowKey="id" columns={columns} dataSource={data} loading={loading} pagination={false} scroll={{ x: 1200 }} />
        <Divider />
        <Pagination
          current={pageNumber}
          pageSize={pageSize}
          total={totalCount}
          showSizeChanger
          onChange={(p, s) => { setPageNumber(p); setPageSize(s); }}
        />
      </Card>
    </div>
  )
}